import { useState, useEffect } from 'react';
import { Gift, Search, Loader2, Check, X, Unlock } from 'lucide-react';
import { apiCall } from './api';
import { toast } from 'sonner';

/** Grâces : l'admin débloque 1 module ou tous les modules pour un abonné. */
export default function AdminGrants({ token, modules = [] }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [granted, setGranted] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiCall('/admin/users', 'GET', null, token).then(setUsers).catch(() => {}); }, [token]);

  const openUser = async (u) => {
    setSel(u);
    try {
      const r = await apiCall(`/admin/grants/${u.id}`, 'GET', null, token);
      setGranted(r.granted || []);
    } catch { setGranted([]); }
  };

  const grant = async (module_id) => {
    setBusy(true);
    try {
      await apiCall('/admin/grant', 'POST', { user_id: sel.id, module_id }, token);
      toast.success(module_id === 'all' ? 'Tous les modules débloqués' : 'Module débloqué');
      await openUser(sel);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const revoke = async (module_id) => {
    setBusy(true);
    try {
      await apiCall('/admin/revoke', 'POST', { user_id: sel.id, module_id }, token);
      toast.success(module_id === 'all' ? 'Accès retirés' : 'Accès retiré');
      await openUser(sel);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const filtered = users.filter(u =>
    !q || (u.name || '').toLowerCase().includes(q.toLowerCase()) || (u.email || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-grants">
      <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><Gift size={18} className="text-yellow-400" />Grâces & déblocages</h3>
      <p className="text-sm text-white/60 mb-4">Offrez l'accès gratuit à un module précis ou à tous les modules pour un abonné.</p>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-3">
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un abonné…"
              className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 pl-9 text-sm" data-testid="grant-search" />
          </div>
          <div className="max-h-96 overflow-y-auto space-y-1">
            {filtered.map(u => (
              <button key={u.id} onClick={() => openUser(u)} data-testid={`grant-user-${u.id}`}
                className={`w-full text-left p-2 rounded-lg text-sm ${sel?.id === u.id ? 'bg-emerald-500/20' : 'hover:bg-white/5'}`}>
                <div className="font-bold truncate">{u.name || '—'}</div>
                <div className="text-xs text-white/50 truncate">{u.email} · {u.country_name || u.country_code || '—'}</div>
              </button>
            ))}
            {filtered.length === 0 && <div className="text-center text-white/40 py-6 text-sm">Aucun abonné</div>}
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          {!sel ? (
            <div className="text-center text-white/40 py-10 text-sm">Sélectionnez un abonné pour gérer ses accès.</div>
          ) : (
            <>
              <div className="mb-3">
                <div className="font-bold">{sel.name}</div>
                <div className="text-xs text-white/50">{sel.email}</div>
              </div>
              <div className="flex gap-2 mb-4">
                <button onClick={() => grant('all')} disabled={busy} data-testid="grant-all"
                  className="flex-1 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 text-slate-950 font-bold py-2 rounded-lg text-sm flex items-center justify-center gap-1"><Unlock size={14} />Tout débloquer</button>
                <button onClick={() => revoke('all')} disabled={busy}
                  className="bg-white/10 hover:bg-white/15 text-white/80 font-bold py-2 px-3 rounded-lg text-sm">Tout retirer</button>
              </div>
              <div className="space-y-2">
                {modules.map(m => {
                  const has = granted.includes(m.id);
                  return (
                    <div key={m.id} className="flex items-center justify-between bg-slate-900/60 rounded-lg p-2">
                      <span className="text-sm truncate">{m.icon} {m.title_fr || m.id}</span>
                      {has ? (
                        <button onClick={() => revoke(m.id)} disabled={busy} data-testid={`revoke-${m.id}`}
                          className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-full flex items-center gap-1"><Check size={12} />Débloqué · retirer</button>
                      ) : (
                        <button onClick={() => grant(m.id)} disabled={busy} data-testid={`grant-${m.id}`}
                          className="text-xs bg-white/10 hover:bg-white/15 px-2 py-1 rounded-full flex items-center gap-1"><Unlock size={12} />Débloquer</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
